'use client';

import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle, memo } from 'react';
import Script from 'next/script';

declare global {
  interface Window {
    hcaptcha: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

interface HCaptchaProps {
  sitekey: string;
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: (message: string) => void;
  theme?: 'light' | 'dark';
  size?: 'normal' | 'compact';
  languageOverride?: string;
}

export interface HCaptchaHandle {
  reset: () => void;
}

const HCaptcha = memo(forwardRef<HCaptchaHandle, HCaptchaProps>(({
  sitekey,
  onVerify,
  onExpire,
  onError,
  theme = 'light',
  size = 'normal',
  languageOverride,
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const cbRef = useRef({ onVerify, onExpire, onError });
  cbRef.current = { onVerify, onExpire, onError };
  const [scriptReady, setScriptReady] = useState(false);

  useImperativeHandle(ref, () => ({
    reset() {
      if (widgetIdRef.current != null && window.hcaptcha) {
        window.hcaptcha.reset(widgetIdRef.current);
      }
    },
  }));

  const renderWidget = useCallback(() => {
    if (!containerRef.current || !window.hcaptcha || widgetIdRef.current !== null) return;
    widgetIdRef.current = window.hcaptcha.render(containerRef.current, {
      sitekey,
      theme,
      size,
      hl: languageOverride,
      callback: (token: string) => cbRef.current.onVerify?.(token),
      'expired-callback': () => cbRef.current.onExpire?.(),
      'error-callback': (err: string) => cbRef.current.onError?.(err),
    });
  }, [sitekey, theme, size, languageOverride]);

  useEffect(() => {
    if (scriptReady && window.hcaptcha) {
      const timer = setTimeout(renderWidget, 0);
      return () => clearTimeout(timer);
    }
  }, [scriptReady, renderWidget]);

  useEffect(() => {
    return () => {
      if (widgetIdRef.current !== null && window.hcaptcha) {
        try {
          window.hcaptcha.remove(widgetIdRef.current);
        } catch {}
        widgetIdRef.current = null;
      }
    };
  }, []);

  if (!sitekey) {
    return (
      <p className="text-sm text-red-600">Configurazione hCaptcha mancante.</p>
    );
  }

  return (
    <>
      <Script
        src="https://js.hcaptcha.com/1/api.js"
        strategy="afterInteractive"
        onLoad={() => setScriptReady(true)}
      />
      <div ref={containerRef} />
    </>
  );
}));

HCaptcha.displayName = 'HCaptcha';

export default HCaptcha;